import { Button, Stack, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import dayjs from 'dayjs';
import { CustomerComplianceDocument } from '@texas/api/endpoints/customersApi';
import {
  complianceDocumentKindLabel,
  complianceDocumentTypeLabel,
} from '@texas/components/configuration/complianceDocumentDefinitions/translations';
import { EntityDirectoryFileThumbnail } from '@texas/components/shared/entityDirectory/EntityDirectoryFileThumbnail';
import { CustomerComplianceDocumentBranches } from './CustomerComplianceDocumentBranches';

interface Props {
  customerArchived: boolean;
  customerId: number;
  t: TFunction;
  onRemove: (document: CustomerComplianceDocument) => void;
}

const columnHelper = createColumnHelper<CustomerComplianceDocument>();

export function customerComplianceDocumentColumns({
  customerArchived,
  customerId,
  t,
  onRemove,
}: Props) {
  return [
    columnHelper.accessor('logo', {
      header: t('complianceDocumentDefinitions.logo'),
      size: 90,
      cell: (info) => {
        const logo = info.getValue();
        if (!logo) return null;

        return (
          <EntityDirectoryFileThumbnail
            file={logo}
            alt={info.row.original.name}
          />
        );
      },
    }),
    columnHelper.accessor('name', {
      header: t('general.name'),
      size: 410,
      cell: (info) => (
        <Stack spacing={1}>
          <Text variant="configuration-strong">{info.getValue()}</Text>
          <Text variant="entity-directory-description">
            {info.row.original.description}
          </Text>
          {info.row.original.archived && (
            <Text variant="entity-directory-status-inactive">
              {t('general.archived')}
            </Text>
          )}
        </Stack>
      ),
    }),
    columnHelper.accessor('kind', {
      header: t('complianceDocumentDefinitions.kindLabel'),
      size: 120,
      cell: (info) => complianceDocumentKindLabel(info.getValue(), t),
    }),
    columnHelper.accessor('type', {
      header: t('complianceDocumentDefinitions.typeLabel'),
      size: 150,
      cell: (info) => complianceDocumentTypeLabel(info.getValue(), t),
    }),
    columnHelper.display({
      id: 'branches',
      header: t('general.branches'),
      size: 280,
      cell: (info) => (
        <CustomerComplianceDocumentBranches
          customerArchived={customerArchived}
          customerId={customerId}
          document={info.row.original}
        />
      ),
    }),
    columnHelper.accessor('added', {
      header: t('customerComplianceDocuments.dateAdded'),
      size: 140,
      cell: (info) => dayjs(info.getValue()).locale('en').format('DD MMM YYYY'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('general.actions'),
      size: 110,
      cell: (info) => (
        <Button
          size="sm"
          variant="entityDirectorySecondary"
          onClick={() => onRemove(info.row.original)}
        >
          {t('general.remove')}
        </Button>
      ),
    }),
  ];
}
